import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { MonthAttribution } from 'src/models/monthly-finance.model';

/** `tidy` rows are written by TIDY from a job or a stay and are read only. */
export type FinancialTransactionSource = 'bank' | 'manual' | 'tidy';

export type FinancialTransactionSourceRecordType = 'Job' | 'GuestReservation';

/** How a row came to point at its stay: TIDY's automatic booking (locked), a guess from the date, or the customer. */
export type GuestReservationLinkOrigin = 'automatic_booking' | 'guess' | 'customer';

/**
 * Where the day the money moved came from: a source recorded it (the bank, a payment, a
 * payout), TIDY stood one in (check-in on a channel stay, a manual row's first day), or
 * the customer corrected it.
 */
export type TransactedAtOrigin = 'recorded' | 'guess' | 'customer';

/** What this member may change on a row. Sent by the Transactions page endpoints, absent on P&L cell details. */
export interface FinancialTransactionPermissions {
  monthly_finance_type: boolean;
  address: boolean;
  period_start_date: boolean;
  /** May spread the row over more days. False on a TIDY row, whose period follows its record. */
  period_end_date: boolean;
  /** May correct the day the money moved. False wherever a source recorded it. */
  transacted_on: boolean;
  description: boolean;
  delete: boolean;
  guest_reservation: boolean;
}

/** One part of a row that merges several charges, e.g. a cleaning plus TIDY's fee on it. */
export interface FinancialTransactionItem {
  label: string;
  /** Absolute cents. */
  amount: number;
  /** Billing reference such as `billing_debit:88120`; absent on parts read from a receipt. */
  ref?: string;
}

export interface FinancialTransactionSuggestion {
  monthly_finance_type_key: string;
  confidence?: 'high' | 'medium' | 'low';
}

/** A row that feeds the Monthly P&L: typed by hand, created from a bank row, or written by TIDY from a job or a stay. */
export interface FinancialTransaction {
  id: number;
  /** YYYY-MM-DD: the first day the amount belongs to. */
  period_start_date: string;
  /** YYYY-MM-DD: the last day it covers; the start itself on a one-day row, the last night on a stay. */
  period_end_date: string;
  /** When the money moved, as an ISO timestamp. Null while nobody has paid. */
  transacted_at: string | null;
  /** YYYY-MM-DD: `transacted_at` as a day at the property. */
  transacted_on: string | null;
  transacted_at_origin: TransactedAtOrigin | null;
  description: string;
  transaction_type: 'Credit' | 'Debit';
  /** Absolute cents. */
  amount: number;
  /**
   * Absolute cents of `amount` that the `year_month` cell counts, so a month's rows add up
   * to its cell. Only sent when the list is filtered by `year_month`.
   */
  month_share_amount?: number;
  currency: string;
  status: 'pending' | 'processed' | 'void';
  source: FinancialTransactionSource;
  address_id: number | null;
  /** On a TIDY row, the job or stay that wrote it. Read only; no endpoint accepts them. */
  source_record_type?: FinancialTransactionSourceRecordType | null;
  source_record_id?: number | null;
  /** The stay the money belongs to, or null. Changed through the guest-reservation-links endpoint. */
  guest_reservation_id?: number | null;
  /** Null on a stay's own row. */
  guest_reservation_link_origin?: GuestReservationLinkOrigin | null;
  permissions?: FinancialTransactionPermissions;
  /** Parts merged into one amount. Empty when a single part made the row. */
  items?: FinancialTransactionItem[];
  monthly_finance_type: { key: string; label: string } | null;
  /** The LLM's guess on a row it could not classify with confidence. */
  suggestion: FinancialTransactionSuggestion | null;
  /** The bank row it came from, when it did. */
  financial_connection_transaction_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface FinancialTransactionParams {
  page?: number;
  per_page?: number;
  /** One address id, or several comma-separated. */
  address_id?: number | string;
  /** Rows whose period starts on or after this day. */
  date_from?: string;
  /** Rows whose period starts on or before this day. */
  date_to?: string;
  /**
   * YYYY-MM: the rows a P&L cell counts in the month. Use instead of the date range when
   * opening a cell.
   */
  year_month?: string;
  /**
   * What `year_month` selects: accrual (default), the rows whose period touches the month;
   * cash, the rows whose money moved in it.
   */
  month_attribution?: MonthAttribution;
  /** One finance type key, or several comma-separated: a P&L group or total. */
  monthly_finance_type_key?: string;
  /** One source, or several comma-separated. */
  source?: FinancialTransactionSource | string;
  needs_attention?: boolean;
  /** Only the rows whose money belongs to this stay. */
  guest_reservation_id?: number;
}

export interface CreateFinancialTransactionBody {
  period_start_date: string;
  /** Omitted, the row covers one day. */
  period_end_date?: string;
  /** Omitted, the money moved on the first day. */
  transacted_on?: string;
  /** Absolute cents. */
  amount: number;
  transaction_type: 'Credit' | 'Debit';
  monthly_finance_type_key: string;
  address_id: number;
  description?: string;
  /** Omitted, the row starts with the stay guessed from its date. */
  guest_reservation_id?: number;
}

/** Each entry changes only the keys it carries. */
export interface UpdateFinancialTransactionEntry {
  id: number;
  monthly_finance_type_key?: string;
  address_id?: number;
  /** Sent alone, the period keeps its length in days. */
  period_start_date?: string;
  period_end_date?: string;
  /** The day the money moved; null gives it back to TIDY's estimate. Alone with `id` on a TIDY row. */
  transacted_on?: string | null;
  description?: string;
}

export interface GuestReservationLinkBody {
  record_type: 'FinancialTransaction' | 'Booking';
  record_id: number;
  /** Null says the record belongs to no stay. */
  guest_reservation_id: number | null;
}

export interface GuestReservationLink {
  id: number;
  guest_reservation_id: number | null;
  record_type: 'FinancialTransaction' | 'Booking';
  record_id: number;
  origin: GuestReservationLinkOrigin;
  locked: boolean;
}

/** Older responses carry no `permissions`; then a TIDY row is read only and a customer row is fully editable. */
export function permissionsOf(txn: FinancialTransaction): FinancialTransactionPermissions {
  if (txn.permissions) { return txn.permissions; }
  const editable = txn.source !== 'tidy';
  return {
    monthly_finance_type: editable,
    address: editable,
    period_start_date: editable,
    period_end_date: editable,
    transacted_on: txn.source === 'manual',
    description: editable,
    delete: editable,
    guest_reservation: editable,
  };
}

const MAX_ENTRIES_PER_PUT = 100;
const ALL_PAGES_PER_PAGE = 200;
const ALL_PAGES_CAP = 25;

export type FinancialTransactionFilters = Omit<FinancialTransactionParams, 'page' | 'per_page'>;

/** A download refuses a list longer than one walk reads, so the file never comes out silently short. */
export class TooManyTransactionsError extends Error {
  constructor() {
    super(`More than ${(ALL_PAGES_CAP * ALL_PAGES_PER_PAGE).toLocaleString('en-US')} transactions match these filters. Pick a shorter date range or fewer properties and try again.`);
  }
}

@Injectable({ providedIn: 'root' })
export class PropertyTransactionsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/financial-transactions`;

  getFinancialTransactions(params: FinancialTransactionParams = {}): Promise<FinancialTransaction[]> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') { continue; }
      httpParams = httpParams.set(key, String(value));
    }
    return firstValueFrom(
      this.http.get<FinancialTransaction[]>(this.baseUrl, { params: httpParams })
    );
  }

  /**
   * Walks every page of a filtered list. Used to cross-reference bank rows with the
   * P&L rows they produced, since the bank row keeps its P&L id even after a delete.
   */
  async getAllFinancialTransactions(params: FinancialTransactionFilters = {}): Promise<FinancialTransaction[]> {
    return (await this.walkPages(params)).rows;
  }

  /** Every row of a filtered list for a download, or a TooManyTransactionsError when the walk stops at its cap. */
  async getFinancialTransactionsForExport(params: FinancialTransactionFilters = {}): Promise<FinancialTransaction[]> {
    const { rows, complete } = await this.walkPages(params);
    if (!complete) { throw new TooManyTransactionsError(); }
    return rows;
  }

  private async walkPages(params: FinancialTransactionFilters): Promise<{ rows: FinancialTransaction[]; complete: boolean }> {
    const rows: FinancialTransaction[] = [];
    for (let page = 1; page <= ALL_PAGES_CAP; page++) {
      const pageRows = await this.getFinancialTransactions({ ...params, page, per_page: ALL_PAGES_PER_PAGE });
      rows.push(...pageRows);
      if (pageRows.length < ALL_PAGES_PER_PAGE) { return { rows, complete: true }; }
    }
    return { rows, complete: false };
  }

  createFinancialTransaction(body: CreateFinancialTransactionBody): Promise<FinancialTransaction> {
    return firstValueFrom(
      this.http.post<FinancialTransaction>(this.baseUrl, body)
    );
  }

  async updateFinancialTransactions(entries: UpdateFinancialTransactionEntry[]): Promise<FinancialTransaction[]> {
    const updated: FinancialTransaction[] = [];
    for (let i = 0; i < entries.length; i += MAX_ENTRIES_PER_PUT) {
      const chunk = entries.slice(i, i + MAX_ENTRIES_PER_PUT);
      const result = await firstValueFrom(
        this.http.put<FinancialTransaction[]>(this.baseUrl, { entries: chunk })
      );
      updated.push(...(result || []));
    }
    return updated;
  }

  deleteFinancialTransaction(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${id}`)
    );
  }

  /**
   * Points a transaction at a stay, or at none. Send the row the user is looking at:
   * on a TIDY job row the backend moves the whole booking, every row of it.
   */
  setGuestReservationLink(body: GuestReservationLinkBody): Promise<GuestReservationLink> {
    return firstValueFrom(
      this.http.put<GuestReservationLink>(`${environment.api_url}api/v1/customer/guest-reservation-links`, body)
    );
  }

  formatAmount(transaction: { amount: number; transaction_type: 'Credit' | 'Debit' }): string {
    const dollars = transaction.amount / 100;
    const formatted = dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return transaction.transaction_type === 'Credit' ? `+${formatted}` : `-${formatted}`;
  }
}
